import { useState, type PropsWithChildren, type ReactNode } from "react";

import { styled } from "@linaria/react";
import Tippy from "@tippyjs/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

export enum DropdownVariant {
  TRANSPARENT = "TRANSPARENT",
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

export enum DropdownPosition {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  TOP_START = "top-start",
  TOP_END = "top-end",
  BOTTOM_START = "bottom-start",
  BOTTOM_END = "bottom-end",
  RIGHT_START = "right-start",
  RIGHT_END = "right-end",
  LEFT_START = "left-start",
  LEFT_END = "left-end",
  AUTO = "auto",
  AUTO_START = "auto-start",
  AUTO_END = "auto-end",
}

export enum DropdownTrigger {
  CLICK = "click",
  HOVER = "hover",
  FOCUS = "focus",
  MANUAL = "manual",
}

export interface DropdownProps {
  variant?: DropdownVariant;
  header?: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
  position?: DropdownPosition;
  offset?: [number, number];
  trigger?: DropdownTrigger;
  width?: number | string;
  isOpen?: boolean;
  onClose?: () => void;
  minWidth?: number | string;
  maxWidth?: number | string;
  fillWidth?: boolean;
  noPadding?: boolean;
  noBorder?: boolean;
  isDisabled?: boolean;
}

const DropdownContent = withTheme(styled.span<
  PropsWithTheme<{
    $variant: DropdownVariant;
    $minWidth?: number | string;
    $maxWidth?: number | string;
    $noPadding: boolean;
    $noBorder: boolean;
    $fillWidth: boolean;
  }>
>`
  display: inline-block;
  position: relative;

  width: ${({ $fillWidth }) => ($fillWidth ? "100%" : "fit-content")};
  min-width: ${({ $minWidth }) => getCssSizeValue($minWidth, "fit-content")};
  max-width: ${({ $maxWidth }) => getCssSizeValue($maxWidth, "none")};
  overflow: ${({ $maxWidth }) => ($maxWidth ? "hidden" : "visible")};

  padding: ${({ $noPadding }) => ($noPadding ? "0" : "4px")};

  background-color: ${({ $variant, theme }) => {
    return match($variant)
      .returnType<string>()
      .with(DropdownVariant.TRANSPARENT, () => "transparent")
      .with(DropdownVariant.BASE, () => theme.color.background.base)
      .with(DropdownVariant.PRIMARY, () => theme.color.background.primary)
      .with(DropdownVariant.SECONDARY, () => theme.color.background.secondary)
      .with(DropdownVariant.TERTIARY, () => theme.color.background.tertiary)
      .exhaustive();
  }};
  border: ${({ $noBorder, $variant, theme }) => {
    if ($noBorder) {
      return "none";
    }

    return match($variant)
      .returnType<string>()
      .with(DropdownVariant.TRANSPARENT, () => "transparent")
      .with(DropdownVariant.BASE, () => `0.5px solid ${theme.color.border.primary}`)
      .with(DropdownVariant.PRIMARY, () => `0.5px solid ${theme.color.border.primary}`)
      .with(DropdownVariant.SECONDARY, () => `0.5px solid ${theme.color.border.primary}`)
      .with(DropdownVariant.TERTIARY, () => `0.5px solid ${theme.color.border.primary}`)
      .exhaustive();
  }};
  border-radius: 5px;
`);

const DropdownTriggerWrapper = styled.span<{
  $isOpen: boolean;
  $fillWidth: boolean;
}>`
  position: relative;
  z-index: ${({ $isOpen }) =>
    $isOpen ? 1001 : "auto"}; /* Below modals (9000) but above most content */
  display: ${({ $fillWidth }) => ($fillWidth ? "block" : "inline-block")};
  width: ${({ $fillWidth }) => ($fillWidth ? "100%" : "auto")};
`;

const Dropdown = ({
  children,
  variant = DropdownVariant.BASE,
  header,
  body,
  footer,
  position = DropdownPosition.BOTTOM_START,
  offset = [0, 6],
  minWidth,
  maxWidth,
  isOpen: controlledIsOpen,
  onClose,
  noPadding = false,
  noBorder = false,
  fillWidth = false,
  isDisabled = false,
}: PropsWithChildren<DropdownProps>) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isControlled = controlledIsOpen !== undefined;
  const visible = isControlled ? controlledIsOpen : internalIsOpen;

  const handleVisibilityChange = (isVisible: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(isVisible);
    }

    if (!isVisible && onClose) {
      onClose();
    }
  };

  const handleClick = () => {
    if (!isControlled) {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const content = (
    <DropdownContent
      $variant={variant}
      $minWidth={minWidth}
      $maxWidth={maxWidth}
      $noPadding={noPadding}
      $noBorder={noBorder}
      $fillWidth={fillWidth}
    >
      {header}
      {body}
      {footer}
    </DropdownContent>
  );

  const handleClickOutside = (instance: { popper: HTMLElement }, event: Event) => {
    const target = event.target as HTMLElement;
    const clickedTippyRoot = target.closest("[data-tippy-root]");

    if (clickedTippyRoot) {
      // Get all tippy roots in DOM order (parent dropdowns come first, children after)
      const allTippyRoots = Array.from(document.querySelectorAll("[data-tippy-root]"));
      const currentIndex = allTippyRoots.indexOf(instance.popper);
      const clickedIndex = allTippyRoots.indexOf(clickedTippyRoot);

      // Only prevent closing if clicked tippy appears AFTER current (is a nested/child dropdown)
      // If clicked tippy appears before (is a parent), allow normal close
      if (clickedIndex > currentIndex && clickedIndex >= 0 && currentIndex >= 0) {
        return;
      }
    }

    handleVisibilityChange(false);
  };

  return (
    <Tippy
      content={content}
      visible={visible}
      onClickOutside={handleClickOutside}
      placement={position}
      offset={offset}
      interactive={true}
      duration={0}
      appendTo={() => document.body}
      disabled={isDisabled}
      zIndex={10000} // Above modals (9000-9001) to allow dropdowns in modals - see z-index scale
    >
      <DropdownTriggerWrapper onClick={handleClick} $isOpen={visible} $fillWidth={fillWidth}>
        {children}
      </DropdownTriggerWrapper>
    </Tippy>
  );
};

export default Dropdown;
